import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { translate } from 'translations/translator';
import { changeMenuSection, changeSection } from 'state/actions/action-creators';
import Box from '@mui/material/Box';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import useFocus from '@lwtears/lwt-common-frontend/lib/@common/hooks/app/use-focus';
const t = (key, options?) => translate('worksheet', key, options);

const WorksheetMenu = ({ menuSectionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const classes = useStyles();
  const location = useLocation();
  const {
    container,
    title,
    buttons,
    selectedButton,
    button,
    buttonText,
    buttonTitle,
    buttonSubtitle,
    icon,
    ariaLiveStatus
  } = classes;
  const sections = useSelector((state: any) => state.worksheetMaker.sections);

  const selectedSectionName = t(sections.find(section => section.id === menuSectionId).name);
  const [focusRef] = useFocus([menuSectionId]);
  const [ariaLiveText, setAriaLiveText] = useState('');

  useEffect(() => {
    setTimeout(() => {
      setAriaLiveText(
        selectedSectionName === 'Home'
          ? selectedSectionName
          : `${selectedSectionName} ${t('worksheets')}`
      );
    }, 100);
  }, [menuSectionId, selectedSectionName]);

  const handleMenuItemClick = sectionId => {
    dispatch(changeMenuSection(sectionId));
    dispatch(changeSection(sectionId));
    const route = `/${sectionId}${location.search}`;

    navigate(route);
  };

  const handleMenuItemKeyDown = (event, sectionId) => {
    const sectionsLength = sections.length;
    let newSectionId;

    if (event.which === 37) {
      newSectionId = (((sectionId - 1) % sectionsLength) + sectionsLength) % sectionsLength;
    } else if (event.which === 13) {
      newSectionId = sectionId;
    } else if (event.which === 39) {
      newSectionId = (sectionId + 1) % sectionsLength;
    } else {
      return;
    }

    handleMenuItemClick(newSectionId);
  };

  const renderSection = section => {
    const isButtonSelected = section.id === menuSectionId;

    return (
      <Box
        {...(isButtonSelected && { ref: focusRef })}
        className={`${button} ${isButtonSelected && selectedButton}`}
        key={section.id}
        tabIndex={0}
        aria-label={
          section.subtitle ? t(section.name) + ' - ' + t(section.subtitle) : t(section.name)
        }
        role="tab"
        aria-selected={isButtonSelected}
        id={`${t(section.name)} Tab`.replace(/ /g, '-')}
        aria-controls={`${t(section.name)} Tab Panel`.replace(/ /g, '-')}
        onClick={() => handleMenuItemClick(section.id)}
        onKeyDown={event => handleMenuItemKeyDown(event, section.id)}
      >
        <Box className={buttonText}>
          <Box className={buttonTitle}>{t(section.name)}</Box>
          <Box className={buttonSubtitle}>{section.subtitle && t(section.subtitle)}</Box>
        </Box>
        {isButtonSelected && <FontAwesomeIcon icon={faArrowRight} className={icon} />}
      </Box>
    );
  };

  return (
    <Box className={container}>
      <h2 className={title}>{t('title')}</h2>
      <Box className={buttons} role="tablist">
        {sections.map(section => renderSection(section))}
      </Box>
      <Box key="ariaLive" className={ariaLiveStatus} aria-live="polite">
        {t('displaying', { content: ariaLiveText })}
      </Box>
    </Box>
  );
};

export default WorksheetMenu;

const useStyles = makeStyles({
  container: {
    margin: 'auto',
    fontFamily: 'Futura-Bold'
  },
  title: {
    fontSize: '24px',
    textAlign: 'center',
    marginTop: '40px',
    marginBottom: '10px',
    '@media (max-width: 600px)': {
      fontSize: '20px',
      marginTop: '10px'
    },
    '@media (max-width: 400px)': {
      fontSize: '10px',
      marginTop: '15px'
    }
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  button: {
    width: '75%',
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '5px',
    padding: '4px 4px 4px 10px',
    marginTop: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    border: '1px solid #aaaaaa'
  },
  selectedButton: {
    color: '#367aba',
    boxShadow: '0 4px 2px -2px gray',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonText: {
    display: 'block'
  },
  buttonTitle: {
    fontSize: '14px',
    '@media (max-width: 600px)': {
      fontSize: '10px'
    },
    '@media (max-width: 400px)': {
      fontSize: '6px'
    }
  },
  buttonSubtitle: {
    display: 'block',
    fontFamily: 'Futura',
    fontSize: '12px',
    '@media (max-width: 600px)': {
      fontSize: '8px'
    }
  },
  icon: {
    marginRight: '20px'
  },
  ariaLiveStatus: {
    position: 'absolute',
    left: '-10000px'
  }
});
